import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Dismiss16Regular, Sparkle16Regular } from '@fluentui/react-icons';
import axiosInstance from '../../apiServices/axios';
import { SUGGEST_RESPONSIBILITIES, USER_PROFILE } from '../../apiServices/api_routes';
import { GetUserData } from '../../redux/usersSlice';
import styles from './css/UserSettings.module.css';
import TimezoneSelect from './TimezoneSelect';

const EMPTY_PROFILE = {
    full_name: '',
    organisation: '',
    job_title: '',
    department: '',
    phone_number: '',
    location: '',
    timezone: '',
};

const normalizeResponsibilities = items => {
    if (!Array.isArray(items)) return [];
    const seen = new Set();
    return items
        .map(item => (typeof item === 'string' ? item.trim() : ''))
        .filter(item => {
            if (!item) return false;
            const key = item.toLowerCase();
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
        });
};

const ProfileSection = () => {
    const dispatch = useDispatch();
    const { user } = useSelector(state => state.user);
    const [profileForm, setProfileForm] = useState(EMPTY_PROFILE);
    const [savedProfile, setSavedProfile] = useState(EMPTY_PROFILE);
    const [responsibilities, setResponsibilities] = useState([]);
    const [savedResponsibilities, setSavedResponsibilities] = useState([]);
    const [suggestedItems, setSuggestedItems] = useState([]);
    const [seenSuggestions, setSeenSuggestions] = useState([]);
    const [selectedSuggestions, setSelectedSuggestions] = useState({});
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [customResponsibility, setCustomResponsibility] = useState('');
    const [profileSaving, setProfileSaving] = useState(false);
    const [suggesting, setSuggesting] = useState(false);

    const hasProfileChanges = JSON.stringify(profileForm) !== JSON.stringify(savedProfile);
    const hasResponsibilityChanges = JSON.stringify(responsibilities) !== JSON.stringify(savedResponsibilities);
    const hasChanges = hasProfileChanges || hasResponsibilityChanges;

    const suggestionHeading = useMemo(() => {
        const role = profileForm.job_title?.trim();
        const department = profileForm.department?.trim();
        if (role && department) return `Suggested for ${role} · ${department}`;
        if (role) return `Suggested for ${role}`;
        return 'Suggested responsibilities';
    }, [profileForm.job_title, profileForm.department]);

    useEffect(() => {
        const nextProfile = {
            full_name: user?.full_name || '',
            organisation: user?.organisation || '',
            job_title: user?.job_title || '',
            department: user?.department || '',
            phone_number: user?.phone_number || '',
            location: user?.location || '',
            timezone: user?.timezone || '',
        };
        const nextResponsibilities = normalizeResponsibilities(user?.responsibilities);
        setProfileForm(nextProfile);
        setSavedProfile(nextProfile);
        setResponsibilities(nextResponsibilities);
        setSavedResponsibilities(nextResponsibilities);
    }, [
        user?.full_name,
        user?.organisation,
        user?.job_title,
        user?.department,
        user?.responsibilities,
        user?.phone_number,
        user?.location,
        user?.timezone,
    ]);

    const handleProfileFieldChange = e => {
        const { name, value } = e.target;
        setProfileForm(prev => ({ ...prev, [name]: value }));
    };

    const handleSuggestResponsibilities = async () => {
        const role = profileForm.job_title?.trim();
        const department = profileForm.department?.trim();
        if (!role || !department) {
            toast.error('Add your role and department before requesting suggestions.');
            return;
        }
        if (suggesting) return;

        setSuggesting(true);
        try {
            const exclude = buildExcludeList();
            const { data } = await axiosInstance.post(SUGGEST_RESPONSIBILITIES, {
                job_title: role,
                department,
                exclude,
            });
            const returned = normalizeResponsibilities(data?.suggestions);
            setSeenSuggestions(prev => normalizeResponsibilities([...prev, ...returned]));
            const available = returned.filter(
                item => !exclude.some(existing => existing.toLowerCase() === item.toLowerCase()),
            );
            setSuggestedItems(available);
            setSelectedSuggestions({});
            setShowSuggestions(true);
            if (!available.length) {
                toast.info('No new suggestions were returned. Try updating your role or department.');
            }
        } catch (error) {
            const msg =
                error?.response?.data?.detail ||
                error?.response?.data?.job_title?.[0] ||
                error?.response?.data?.department?.[0] ||
                'Failed to generate responsibility suggestions';
            toast.error(typeof msg === 'string' ? msg : 'Failed to generate responsibility suggestions');
        } finally {
            setSuggesting(false);
        }
    };

    const toggleSuggestion = label => {
        setSelectedSuggestions(prev => ({ ...prev, [label]: !prev[label] }));
    };

    const handleAddSelectedSuggestions = () => {
        const toAdd = suggestedItems.filter(item => selectedSuggestions[item]);
        if (!toAdd.length) return;
        setResponsibilities(prev => normalizeResponsibilities([...prev, ...toAdd]));
        setShowSuggestions(false);
        setSuggestedItems([]);
        setSelectedSuggestions({});
    };

    const handleDismissSuggestions = () => {
        setShowSuggestions(false);
        setSuggestedItems([]);
        setSelectedSuggestions({});
    };

    const buildExcludeList = () => normalizeResponsibilities([...responsibilities, ...seenSuggestions]);

    const handleAddCustomResponsibility = () => {
        const trimmed = customResponsibility.trim();
        if (!trimmed) return;
        const exists = responsibilities.some(item => item.toLowerCase() === trimmed.toLowerCase());
        if (exists) {
            toast.info('That responsibility is already in your list');
            return;
        }
        setResponsibilities(prev => [...prev, trimmed]);
        setCustomResponsibility('');
    };

    const handleRemoveResponsibility = itemToRemove => {
        setResponsibilities(prev => prev.filter(item => item !== itemToRemove));
    };

    const handleSave = async () => {
        if (profileSaving || !hasChanges) return;
        setProfileSaving(true);
        try {
            const payload = {
                ...profileForm,
                ...(hasResponsibilityChanges ? { responsibilities } : {}),
            };
            if (payload.timezone) {
                payload.timezone_source = 'USER';
            }
            if (payload.location) {
                payload.location_source = 'USER';
            }
            await axiosInstance.patch(USER_PROFILE, payload);
            await dispatch(GetUserData());
            setSavedProfile(profileForm);
            setSavedResponsibilities(responsibilities);
            handleDismissSuggestions();
            toast.success('Profile updated successfully');
        } catch (error) {
            const msg =
                error?.response?.data?.full_name?.[0] ||
                error?.response?.data?.job_title?.[0] ||
                error?.response?.data?.organisation?.[0] ||
                error?.response?.data?.department?.[0] ||
                error?.response?.data?.responsibilities?.[0] ||
                error?.response?.data?.error ||
                error?.response?.data?.detail ||
                'Failed to update profile';
            toast.error(typeof msg === 'string' ? msg : 'Failed to update profile');
        } finally {
            setProfileSaving(false);
        }
    };

    const selectedSuggestionCount = suggestedItems.filter(item => selectedSuggestions[item]).length;

    return (
        <>
            <header className={styles.pageHeader}>
                <h1 className={styles.pageTitle}>Profile</h1>
                <p className={styles.pageDescription}>Manage your profile information and help Lens understand your role.</p>
            </header>

            <section className={styles.card}>
                <div className={styles.cardHeader}>
                    <h2 className={styles.cardTitle}>Profile information</h2>
                </div>
                <div className={styles.cardBody}>
                    <div className={styles.fieldGrid}>
                        <label className={styles.field}>
                            <span className={styles.fieldLabel}>
                                Full Name <span className={styles.requiredMark}>*</span>
                            </span>
                            <input
                                className={styles.textInput}
                                name="full_name"
                                value={profileForm.full_name}
                                onChange={handleProfileFieldChange}
                                placeholder="Enter your full name"
                            />
                        </label>
                        <label className={styles.field}>
                            <span className={styles.fieldLabel}>
                                Organisation <span className={styles.requiredMark}>*</span>
                            </span>
                            <input
                                className={styles.textInput}
                                name="organisation"
                                value={profileForm.organisation}
                                onChange={handleProfileFieldChange}
                                placeholder="Enter your organisation"
                            />
                        </label>
                        <label className={styles.field}>
                            <span className={styles.fieldLabel}>
                                Role <span className={styles.requiredMark}>*</span>
                            </span>
                            <input
                                className={styles.textInput}
                                name="job_title"
                                value={profileForm.job_title}
                                onChange={handleProfileFieldChange}
                                placeholder="Enter your role"
                            />
                        </label>
                        <label className={styles.field}>
                            <span className={styles.fieldLabel}>
                                Department <span className={styles.requiredMark}>*</span>
                            </span>
                            <input
                                className={styles.textInput}
                                name="department"
                                value={profileForm.department}
                                onChange={handleProfileFieldChange}
                                placeholder="Enter your department"
                            />
                        </label>
                    </div>
                </div>
            </section>

            <section className={styles.card}>
                <div className={styles.cardHeader}>
                    <h2 className={styles.cardTitle}>Contact & location</h2>
                    <p className={styles.cardDescription}>
                        Used in The Tent so colleagues know where you are working from and how to reach you.
                    </p>
                </div>
                <div className={styles.cardBody}>
                    <div className={styles.fieldGrid}>
                        <label className={styles.field}>
                            <span className={styles.fieldLabel}>Phone</span>
                            <input
                                className={styles.textInput}
                                name="phone_number"
                                value={profileForm.phone_number}
                                onChange={handleProfileFieldChange}
                                placeholder="Optional contact number"
                            />
                        </label>
                        <label className={styles.field}>
                            <span className={styles.fieldLabel}>Working location</span>
                            <input
                                className={styles.textInput}
                                name="location"
                                value={profileForm.location}
                                onChange={handleProfileFieldChange}
                                placeholder="e.g. London, UK"
                            />
                        </label>
                        <label className={styles.field}>
                            <span className={styles.fieldLabel}>Timezone</span>
                            <TimezoneSelect
                                value={profileForm.timezone}
                                onChange={handleProfileFieldChange}
                            />
                        </label>
                    </div>
                </div>
            </section>

            <section className={styles.card}>
                <div className={styles.cardHeader}>
                    <h2 className={styles.cardTitle}>Responsibilities</h2>
                    <p className={styles.cardDescription}>
                        Add the responsibilities most relevant to your role. Lens can use these to better understand your priorities and
                        personalise future AI assistance.
                    </p>
                </div>
                <div className={styles.cardBody}>
                    <div className={styles.responsibilitiesBlock}>
                        <p className={styles.sectionEyebrow}>Your responsibilities</p>
                        {responsibilities.length === 0 ? (
                            <p className={styles.emptyStateText}>No responsibilities added yet.</p>
                        ) : (
                            <ul className={styles.responsibilityList}>
                                {responsibilities.map(item => (
                                    <li key={item} className={styles.responsibilityItem}>
                                        <span>{item}</span>
                                        <button
                                            type="button"
                                            className={styles.responsibilityRemoveButton}
                                            onClick={() => handleRemoveResponsibility(item)}
                                            aria-label={`Remove ${item}`}>
                                            <Dismiss16Regular aria-hidden />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    <button
                        type="button"
                        className={styles.secondaryButton}
                        onClick={handleSuggestResponsibilities}
                        disabled={suggesting}>
                        <Sparkle16Regular aria-hidden />
                        {suggesting ? 'Suggesting…' : showSuggestions ? 'Suggest More' : 'Suggest Responsibilities'}
                    </button>

                    {showSuggestions && suggestedItems.length > 0 ? (
                        <div className={styles.suggestionsBlock}>
                            <p className={styles.sectionEyebrow}>{suggestionHeading}</p>
                            <ul className={styles.suggestionList}>
                                {suggestedItems.map(item => (
                                    <li key={item}>
                                        <label className={styles.suggestionRow}>
                                            <input
                                                type="checkbox"
                                                checked={Boolean(selectedSuggestions[item])}
                                                onChange={() => toggleSuggestion(item)}
                                                className={styles.suggestionCheckbox}
                                            />
                                            <span className={styles.suggestionText}>{item}</span>
                                        </label>
                                    </li>
                                ))}
                            </ul>
                            <div className={styles.suggestionActions}>
                                <button
                                    type="button"
                                    className={styles.secondaryButton}
                                    disabled={selectedSuggestionCount === 0}
                                    onClick={handleAddSelectedSuggestions}>
                                    Add Selected
                                </button>
                                <button type="button" className={styles.textButton} onClick={handleDismissSuggestions}>
                                    Dismiss Suggestions
                                </button>
                            </div>
                        </div>
                    ) : null}

                    {showSuggestions && !suggesting && suggestedItems.length === 0 ? (
                        <p className={styles.emptyStateText}>No additional suggestions available.</p>
                    ) : null}

                    <div className={styles.addOwnBlock}>
                        <p className={styles.sectionEyebrow}>Add your own</p>
                        <div className={styles.addOwnRow}>
                            <input
                                className={styles.textInput}
                                value={customResponsibility}
                                onChange={e => setCustomResponsibility(e.target.value)}
                                placeholder="Enter a responsibility"
                                onKeyDown={e => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleAddCustomResponsibility();
                                    }
                                }}
                            />
                            <button
                                type="button"
                                className={styles.secondaryButton}
                                disabled={!customResponsibility.trim()}
                                onClick={handleAddCustomResponsibility}>
                                Add
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            <div className={styles.pageActions}>
                <button
                    type="button"
                    className={styles.primaryButton}
                    disabled={!hasChanges || profileSaving}
                    onClick={handleSave}>
                    {profileSaving ? 'Saving…' : 'Save Changes'}
                </button>
            </div>
        </>
    );
};

export default ProfileSection;
